import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { determineAppMode } from "@/lib/auth-utils";
import { FullScreenLoader } from "@/components/PulseLoader";

interface TrainerGuardProps {
  children: React.ReactNode;
}

/**
 * Guards trainer routes from client access.
 * Shows loading state while checking, then redirects clients to portal.
 * This prevents clients from ever seeing the trainer navbar.
 */
export function TrainerGuard({ children }: TrainerGuardProps) {
  const { user, session, isLoading: authLoading, lastEvent } = useAuth();
  const [status, setStatus] = useState<"loading" | "authorized" | "redirecting">("loading");
  const navigate = useNavigate();
  const mountedRef = useRef(true);
  const sessionRef = useRef(session);

  // Keep session ref current without triggering re-renders
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  const checkAccess = useCallback(async () => {
    // Use a fresh session from Supabase instead of the ref — the ref may
    // not have updated yet on the very first render after OAuth redirect.
    const { data: { session: freshSession } } = await supabase.auth.getSession();
    const currentSession = freshSession ?? sessionRef.current;
    if (!currentSession) {
      // Don't redirect during an OAuth code exchange — Supabase is still
      // processing the ?code= param and the session will arrive shortly.
      const url = new URL(window.location.href);
      if (url.searchParams.has("code") || window.location.hash.includes("access_token")) {
        return;
      }
      navigate("/auth", { replace: true });
      return;
    }

    try {
      const result = await determineAppMode(currentSession);

      if (!mountedRef.current) return;

      if (result.mode === "client") {
        setStatus("redirecting");
        navigate("/portal/dashboard", { replace: true });
      } else {
        // Check if onboarding is completed
        const { data: profile } = await supabase
          .from("profiles")
          .select("onboarding_completed")
          .eq("id", currentSession.user.id)
          .single();

        if (profile && !profile.onboarding_completed) {
          setStatus("redirecting");
          navigate("/onboarding", { replace: true });
          return;
        }

        // Even if onboarding_completed is true, make sure the user has an
        // organization membership. Public-signup users land here with no org
        // row until the onboarding wizard creates one; if they somehow skip
        // past that step (or their org insert failed), bounce them back.
        // Note: `.limit(1)` (no `.single()`) so users with multiple org
        // memberships don't trip a "too many rows" error here.
        const { data: memberships } = await supabase
          .from("organization_members")
          .select("organization_id")
          .eq("user_id", currentSession.user.id)
          .order("created_at", { ascending: true })
          .limit(1);

        if (!memberships || memberships.length === 0) {
          setStatus("redirecting");
          navigate("/onboarding", { replace: true });
          return;
        }

        setStatus("authorized");
      }
    } catch (error) {
      console.error("[TrainerGuard] Error checking access:", error);
      if (mountedRef.current) {
        setStatus("authorized");
      }
    }
  }, [navigate]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Check access when user changes (login) — not on token refresh
  useEffect(() => {
    if (!authLoading && user?.id) {
      checkAccess();
    } else if (!authLoading && !user) {
      // Don't redirect during OAuth code exchange — session is still being established
      const url = new URL(window.location.href);
      const isOAuthReturn = url.searchParams.has("code") || window.location.hash.includes("access_token");
      if (!isOAuthReturn) {
        navigate("/auth", { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user?.id, checkAccess]);

  // Handle sign-out events
  useEffect(() => {
    if (lastEvent === "SIGNED_OUT") {
      navigate("/auth", { replace: true });
    }
  }, [lastEvent, navigate]);

  // Show loading spinner while checking access
  if (authLoading || status === "loading" || status === "redirecting") {
    return (
      <FullScreenLoader
        message={status === "redirecting" ? "Redirecting to portal..." : undefined}
      />
    );
  }

  return <>{children}</>;
}
